import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from 'qampo-ui-library';
import { Button } from '../UI/base/Button';
import type { InterventionGroupDto, TreatmentPlanDto } from '../../api/generated';
import { AffectedGroupsPicker } from './AffectedGroupsPicker';
import { computeAffectedGroupIds, DEFAULT_AFFECTED_GROUPS_CHOICE } from './affectedGroups';
import styles from './AffectedGroupsPopover.module.css';

interface AffectedGroupsPopoverProps {
  anchorRef: RefObject<HTMLButtonElement | null>;
  plan: TreatmentPlanDto;
  selectedGroup: InterventionGroupDto;
  title: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (groupIds: string[]) => void;
}

const POPOVER_GAP_PX = 6;
const VIEWPORT_PADDING_PX = 8;

export const AffectedGroupsPopover = ({
  anchorRef,
  plan,
  selectedGroup,
  title,
  confirmLabel,
  onClose,
  onConfirm
}: AffectedGroupsPopoverProps) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [groupIds, setGroupIds] = useState<string[]>(() =>
    computeAffectedGroupIds(plan, selectedGroup, DEFAULT_AFFECTED_GROUPS_CHOICE)
  );
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const computePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;
    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    let top = anchorRect.bottom + POPOVER_GAP_PX;
    if (top + popoverRect.height > window.innerHeight - VIEWPORT_PADDING_PX) {
      top = anchorRect.top - popoverRect.height - POPOVER_GAP_PX;
    }
    let left = anchorRect.right - popoverRect.width;
    if (left < VIEWPORT_PADDING_PX) left = VIEWPORT_PADDING_PX;
    if (left + popoverRect.width > window.innerWidth - VIEWPORT_PADDING_PX) {
      left = window.innerWidth - popoverRect.width - VIEWPORT_PADDING_PX;
    }
    setPosition({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    computePosition();
    window.addEventListener('resize', computePosition);
    return () => window.removeEventListener('resize', computePosition);
  }, [computePosition]);

  useClickOutside(popoverRef, (event) => {
    const target = event?.target;
    if (target instanceof Node && anchorRef.current?.contains(target)) return;
    onClose();
  });

  useEffect(() => {
    // Capture-phase + stopPropagation so the drawer's document-level Escape
    // listener doesn't also fire and close the drawer behind this popover.
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={
        position
          ? { top: position.top, left: position.left }
          : { visibility: 'hidden' }
      }
      role="dialog"
    >
      <h4 className={styles.title}>{title}</h4>
      <AffectedGroupsPicker plan={plan} selectedGroup={selectedGroup} onChange={setGroupIds} />
      <div className={styles.footer}>
        <Button type="secondary" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={() => onConfirm(groupIds)}
          disabled={groupIds.length === 0}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>,
    document.body
  );
};
